import type { MemoizedFn } from 'memoize-one';
import memoize from 'memoize-one';

import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension/extensionPluginType';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility/types';

import { createReferentiality } from '@atlassian/editor-referentiality/referentiality-plugin';
import type { ReferentialityContext, ReferentialityPluginOptions } from '@atlassian/editor-referentiality/types';

import { getEditorAnalyticsAPI } from '@confluence/editor-features/entry-points/getEditorAnalyticsAPI.ts';

import { getConnectDataListener } from './connectDataListener.ts';

export const getReferentialityContext: MemoizedFn<
	(
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		contentId: string,
		appearance?: EditorAppearance,
		extensionHandlers?: ExtensionHandlers,
		__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'],
		getExtensionHeight?: GetPMNodeHeight,
	) => ReferentialityContext
> = memoize(
	(
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		contentId: string,
		appearance: EditorAppearance = 'full-page',
		extensionHandlers?: ExtensionHandlers,
		__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'],
		getExtensionHeight?: GetPMNodeHeight,
	): ReferentialityContext => {
		const editorAnalyticsAPI = getEditorAnalyticsAPI(contentId, createAnalyticsEvent);

		const options: ReferentialityPluginOptions = {
			editorAnalyticsAPI,
			appearance,
			featureFlags: {
				confluence_frontend_macro_interaction_design: true,
			},
			externalObservers: [getConnectDataListener()],
			extensionHandlers,
			__rendererExtensionOptions,
			getExtensionHeight,
		};

		return { ...createReferentiality(options), options };
	},
);
